// client/src/components/BidStatusBanner.js
import React, { useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';

const pill = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: '999px',
  fontSize: 12,
  fontWeight: 600,
  color: '#fff',
};

// Backwards-compatible banner:
// - Old props: { active, endCountdown, nextCountdown, nextDateTimeLabel }
// - New props: { active, startTime, endTime, nextStartMs, nextDateTimeLabel }
// If countdown objects are not provided, this component will compute them locally
// and tick once per second without forcing the whole page to re-render.
export default function BidStatusBanner({
  active,
  startTime,
  endTime,
  nextStartMs,
  endCountdown,
  nextCountdown,
  nextDateTimeLabel,
}) {
  const needsLocalTick = useMemo(() => {
    return (!endCountdown && !!endTime) || (!nextCountdown && typeof nextStartMs === 'number');
  }, [endCountdown, endTime, nextCountdown, nextStartMs]);

  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!needsLocalTick) return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [needsLocalTick]);

  const computedEnd = useMemo(() => {
    if (endCountdown) return endCountdown;
    if (!endTime) return null;

    const now = DateTime.utc();
    const end = DateTime.fromISO(endTime, { setZone: true }).toUTC();
    if (!end.isValid) return null;

    if (now > end) return { h: 0, m: 0, s: 0 };

    const diff = end.diff(now, ['hours', 'minutes', 'seconds']).toObject();
    return {
      h: Math.max(0, Math.floor(diff.hours ?? 0)),
      m: Math.max(0, Math.floor(diff.minutes ?? 0)),
      s: Math.max(0, Math.floor(diff.seconds ?? 0)),
    };
  }, [endCountdown, endTime, tick]);

  const computedNext = useMemo(() => {
    if (nextCountdown) return nextCountdown;
    if (typeof nextStartMs !== 'number') return null;

    const nowMs = DateTime.utc().toMillis();
    if (nowMs >= nextStartMs) return { d: 0, h: 0, m: 0, s: 0 };

    const start = DateTime.fromMillis(nextStartMs, { zone: 'utc' });
    const now = DateTime.utc();
    const diff = start.diff(now, ['days', 'hours', 'minutes', 'seconds']).toObject();
    return {
      d: Math.max(0, Math.floor(diff.days ?? 0)),
      h: Math.max(0, Math.floor(diff.hours ?? 0)),
      m: Math.max(0, Math.floor(diff.minutes ?? 0)),
      s: Math.max(0, Math.floor(diff.seconds ?? 0)),
    };
  }, [nextCountdown, nextStartMs, tick]);

  if (active) {
    const { h = 0, m = 0, s = 0 } = computedEnd || {};
    return (
      <div style={{ margin: '6px 0' }}>
        <span style={{ ...pill, background: '#16a34a', marginRight: 8 }}>Bidding is LIVE</span>
        <span style={{ ...pill, background: '#334155' }}>
          Ends in {`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`}
        </span>
      </div>
    );
  }

  if (computedNext) {
    const { d = 0, h = 0, m = 0, s = 0 } = computedNext;
    const dhms = `${d}d ${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
    return (
      <div style={{ margin: '6px 0' }}>
        <span style={{ ...pill, background: '#0ea5e9', marginRight: 8 }}>Next bid starts in</span>
        <span style={{ ...pill, background: '#334155', marginRight: 8 }}>{dhms}</span>
        {nextDateTimeLabel ? <span style={{ color: '#475569', fontSize: 12 }}>({nextDateTimeLabel})</span> : null}
      </div>
    );
  }

  return (
    <div style={{ margin: '6px 0' }}>
      <span style={{ ...pill, background: '#94a3b8' }}>Bidding not live</span>
    </div>
  );
}
